<template>
  <n-card title="Expense Management">
    <n-space vertical>
      <n-table :bordered="false" :single-line="false">
        <thead>
          <tr>
            <th>Name</th>
            <th>Category</th>
            <th>Amount</th>
            <th>Periodicity</th>
            <th>Target</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="expense in expenses" :key="expense.id">
            <td>{{ expense.name }}</td>
            <td>
              <n-tag v-if="expense.category" size="small" :color="getCategoryColor(expense.category)">
                {{ expense.category }}
              </n-tag>
              <span v-else style="color: #666">—</span>
            </td>
            <td>{{ formatCurrency(expense.amount) }}</td>
            <td>
              <n-tag size="small" :type="getPeriodicityType(expense.periodicity)">
                {{ expense.periodicity }}
              </n-tag>
            </td>
            <td>
              <n-tag size="small" :type="getTargetType(expense.account_type_target)">
                {{ expense.account_type_target }}
              </n-tag>
            </td>
            <td>{{ formatDate(expense.date) }}</td>
            <td>{{ expense.end_date ? formatDate(expense.end_date) : '-' }}</td>
            <td>
              <n-space>
                <n-button size="small" @click="editExpense(expense)">Edit</n-button>
                <n-button size="small" type="error" @click="deleteExpense(expense.id)">Delete</n-button>
              </n-space>
            </td>
          </tr>
        </tbody>
      </n-table>
      <n-button @click="showExpenseForm = true">Add Expense</n-button>
      <n-modal 
        v-model:show="showExpenseForm"
        preset="card"
        :mask-closable="true" 
        :title="isEditing ? 'Edit Expense' : 'Add Expense'"
        style="width: 60%; max-width: 600px"
      >
        <n-form 
          ref="formRef" 
          :model="formExpense" 
          label-placement="left"
          label-width="120"
        >
          <n-form-item label="Name" path="name">
            <n-input v-model:value="formExpense.name" />
          </n-form-item>
          <n-form-item label="Category" path="category">
            <n-select v-model:value="formExpense.category" :options="categoryOptions" clearable />
          </n-form-item>
          <n-form-item label="Periodicity" path="periodicity">
            <n-select v-model:value="formExpense.periodicity" :options="periodicityOptions" />
          </n-form-item>
          <n-form-item label="Account Type" path="account_type_target">
            <n-select v-model:value="formExpense.account_type_target" :options="accountTypeOptions" />
          </n-form-item>
          <n-form-item label="Start Date" path="date">
            <n-date-picker 
              v-model:value="formExpense.date" 
              type="date"
            />
          </n-form-item>
          <n-form-item label="End Date" path="end_date">
            <n-date-picker 
              v-model:value="formExpense.end_date" 
              type="date"
              :disabled="formExpense.periodicity === 'One-off'"
            />
          </n-form-item>
            <n-form-item label="Amount" path="amount">
            <n-input-number 
              v-model:value="formExpense.amount" 
              :min="0"
              :precision="2"
              style="width: 100%"
            />
          </n-form-item>
          <div style="text-align: right">
            <n-button type="primary" @click="submitForm">
              {{ isEditing ? 'Update' : 'Add' }}
            </n-button>
          </div>
        </n-form>
      </n-modal>
    </n-space>
  </n-card>
</template>

<script setup>
import { useExpenseStore } from '../stores/expenseStore';
import {
  formatCurrency, formatDate, getCategoryColor,
  getPeriodicityType, getTargetType,
  CATEGORY_OPTIONS, PERIODICITY_OPTIONS, ACCOUNT_TYPE_OPTIONS,
} from '../utils/format';
import { onMounted, computed, ref } from 'vue';

const expenseStore = useExpenseStore();
const expenses = computed(() => expenseStore.expenses);
const showExpenseForm = ref(false);
const isEditing = ref(false);
const editingId = ref(null);

const categoryOptions = CATEGORY_OPTIONS;
const periodicityOptions = PERIODICITY_OPTIONS;
const accountTypeOptions = ACCOUNT_TYPE_OPTIONS;

const formExpense = ref({
  name: '',
  amount: 0,
  periodicity: null,
  account_type_target: null,
  category: null,
  date: null,
  end_date: null
});

onMounted(() => {
  expenseStore.fetchExpenses();
});


const resetForm = () => {
  formExpense.value = {
    name: '',
    amount: 0,
    periodicity: null,
    account_type_target: null,
    category: null,
    date: null,
    end_date: null
  };
  isEditing.value = false;
  editingId.value = null;
  showExpenseForm.value = false;
};

const deleteExpense = async (id) => {
  try {
    await expenseStore.deleteExpense(id);
  } catch (error) {
    console.error('Error deleting expense:', error);
  }
};

const editExpense = (expense) => {
  isEditing.value = true;
  editingId.value = expense.id;
  formExpense.value = {
    ...expense,
    date: expense.date ? new Date(expense.date).getTime() : null,
    end_date: expense.end_date ? new Date(expense.end_date).getTime() : null
  };
  showExpenseForm.value = true;
};

const submitForm = async () => {
  try {
    if (isEditing.value) {
      await expenseStore.updateExpense(editingId.value, formExpense.value);
    } else {
      await expenseStore.addExpense(formExpense.value);
    }
    resetForm();
  } catch (error) {
    console.error('Error submitting form:', error);
  }
};

</script>